import { REDEEM_FREE_RUNESTONE_MUTATION } from "@/graphql/mutations/playerMutations";
import {
    FREE_RUNESTONE_CLAIMS_QUERY,
    PLAYER_RUNESTONE_CABINET_QUERY,
} from "@/graphql/queries/profileQueries";
import { classNamesExtended } from "@/helpers/common/classNames";
import { formatNumber } from "@/helpers/common/formatNumber";
import { convertLetters } from "@/utils/letterConversion";
import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";

interface RunestoneCabinetEntry {
    hasRedeemed: boolean;
    redemptionCount: number;
    ravenguardPoints: number;
    cooldownExpiresAt?: string | null;
    cooldownRemainingSeconds: number;
    streamerUrl?: string | null;
    streamTeamMember: {
        id: string;
        twitchUsername?: string | null;
        isAcademy: boolean;
        player: {
            id: string;
            name: string;
        };
    };
    runestone: {
        id: string;
        runeName: string;
        imageUrl: string | null;
    } | null;
}

interface PlayerRunestoneCabinetQueryData {
    playerRunestoneCabinet: RunestoneCabinetEntry[];
}

interface FreeRunestoneClaimsQueryData {
    playerStats: {
        availableFreeRunestoneClaims: number;
    };
    featureFlags: {
        freeRunestoneClaimsEnabled: boolean;
    };
}

interface RunestoneCardProps {
    entry: RunestoneCabinetEntry;
}

function formatCooldown(seconds: number): string {
    if (seconds <= 0) {
        return "Ready";
    }

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    return `${days}d ${hours}h ${minutes}m`;
}

function getRavenguardBaseUrl(): string {
    const hostname = window.location.hostname.toLowerCase();
    const isLocalEnv =
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname.endsWith(".local");

    if (isLocalEnv) {
        return "http://ravenguard.vanderheim.local:5176";
    }

    return "https://ravenguard.vanderheim.com";
}

function getStreamerProfileUrl(entry: RunestoneCabinetEntry): string | null {
    const username = entry.streamTeamMember.twitchUsername?.trim();
    if (!username) {
        return entry.streamerUrl ?? null;
    }

    return `${getRavenguardBaseUrl()}/team/${encodeURIComponent(username)}`;
}

function RunestoneCard({ entry }: RunestoneCardProps) {
    const [hovered, setHovered] = useState(false);
    const flipped = hovered;

    const displayName = entry.streamTeamMember.player.name;
    const imageUrl = entry.runestone?.imageUrl;

    const CardContent = (
        <div
            className={classNamesExtended(
                "border-brand relative aspect-square w-full max-w-32.5 cursor-pointer overflow-hidden rounded border-4 sm:max-w-none",
                {
                    "border-gray-300": !entry.hasRedeemed,
                },
            )}
            style={{ perspective: "1000px" }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            title={`${displayName} (${entry.hasRedeemed ? "Redeemed" : "Not redeemed"})`}
        >
            <div
                className="relative h-full w-full transform transition-transform duration-700"
                style={{
                    transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                    transformStyle: "preserve-3d",
                }}
            >
                <div
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/20 p-2"
                    style={{ backfaceVisibility: "hidden" }}
                >
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={`${displayName} Runestone`}
                            className={classNamesExtended(
                                "max-h-[70%] max-w-full object-contain",
                                {
                                    grayscale: !entry.hasRedeemed,
                                },
                            )}
                        />
                    ) : (
                        <div className="flex h-[70%] w-full items-center justify-center rounded bg-slate-800/60 px-2 text-center text-xs text-slate-300">
                            No Runestone
                        </div>
                    )}
                    <p className="text-center text-xs font-semibold text-slate-100 sm:text-sm">
                        {convertLetters(displayName)}
                    </p>
                </div>

                <div
                    className="bg-brand_charcoal text-brand absolute inset-0 flex flex-col items-center justify-center rounded p-2 text-xs sm:text-sm"
                    style={{
                        backfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                    }}
                >
                    <h3 className="font-prviking mb-2 text-center text-base font-bold sm:text-lg">
                        {convertLetters(displayName)}
                    </h3>
                    <div className="space-y-1 text-left">
                        <p>
                            <strong>STATUS:</strong>{" "}
                            <span className="text-stats">
                                {entry.hasRedeemed
                                    ? "Redeemed"
                                    : "Not Redeemed"}
                            </span>
                        </p>
                        <p>
                            <strong>RP:</strong>{" "}
                            <span className="text-stats">
                                {formatNumber(entry.ravenguardPoints || 0)}
                            </span>
                        </p>
                        <p>
                            <strong>REDEMPTIONS:</strong>{" "}
                            <span className="text-stats">
                                {formatNumber(entry.redemptionCount || 0)}
                            </span>
                        </p>
                        <p>
                            <strong>COOLDOWN:</strong>{" "}
                            <span className="text-stats">
                                {formatCooldown(
                                    entry.cooldownRemainingSeconds || 0,
                                )}
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const streamerProfileUrl = getStreamerProfileUrl(entry);

    if (!streamerProfileUrl) {
        return CardContent;
    }

    return (
        <a href={streamerProfileUrl} className="block">
            {CardContent}
        </a>
    );
}

export default function RunestonesCabinet() {
    const { data, loading, error, refetch } =
        useQuery<PlayerRunestoneCabinetQueryData>(
            PLAYER_RUNESTONE_CABINET_QUERY,
            {
                fetchPolicy: "cache-and-network",
            },
        );

    const { data: claimsData, refetch: refetchClaims } =
        useQuery<FreeRunestoneClaimsQueryData>(FREE_RUNESTONE_CLAIMS_QUERY, {
            fetchPolicy: "cache-and-network",
        });

    const [redeemFreeRunestone, { loading: redeemLoading }] = useMutation(
        REDEEM_FREE_RUNESTONE_MUTATION,
        {
            onCompleted: async () => {
                await Promise.all([refetch(), refetchClaims()]);
            },
            onError: (error) => {
                console.error("Failed to redeem free runestone:", error);
            },
        },
    );

    const [selectedMemberId, setSelectedMemberId] = useState<string | null>(
        null,
    );
    const [showModal, setShowModal] = useState(false);

    if (loading && !data) {
        return <p>Loading...</p>;
    }

    if (error && data) {
        console.warn("Runestones cabinet refresh failed, showing cached data.");
    }

    const entries = data?.playerRunestoneCabinet ?? [];
    const availableClaims =
        claimsData?.playerStats?.availableFreeRunestoneClaims ?? 0;
    const claimsEnabled =
        claimsData?.featureFlags?.freeRunestoneClaimsEnabled ?? false;

    const handleClaimSelect = (memberId: string) => {
        setSelectedMemberId(memberId);
        setShowModal(false);

        redeemFreeRunestone({
            variables: {
                streamTeamMemberId: memberId,
            },
        });
    };

    return (
        <div className="w-full space-y-6">
            {/* Free Runestone Claims Section */}
            {claimsEnabled &&
            availableClaims > 0 &&
            entries.some((e) => !e.streamTeamMember.isAcademy) ? (
                <div className="border-brand/50 bg-brand_charcoal/50 rounded border p-4">
                    <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                        <div>
                            <h3 className="font-prviking text-brand mb-1 text-lg font-bold">
                                Free Runestone Claim Available
                            </h3>
                            <p className="text-sm text-slate-300">
                                You have{" "}
                                <span className="text-stats font-bold">
                                    {availableClaims}
                                </span>{" "}
                                free claim{availableClaims !== 1 ? "s" : ""} to
                                redeem!
                            </p>
                        </div>
                        <button
                            onClick={() => setShowModal(true)}
                            disabled={redeemLoading}
                            className="bg-brand text-brand_charcoal hover:bg-brand/90 rounded px-4 py-2 font-bold whitespace-nowrap disabled:opacity-50"
                        >
                            {redeemLoading ? "Claiming..." : "Claim Now"}
                        </button>
                    </div>

                    {/* Modal for selecting stream team member */}
                    {showModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                            <div className="bg-brand_charcoal max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg p-6">
                                <h4 className="text-brand mb-4 text-lg font-bold">
                                    Select a Runestone to Claim
                                </h4>
                                <p className="mb-4 text-sm text-slate-300">
                                    Which streamer's Runestone would you like to
                                    claim?
                                </p>
                                <div className="space-y-2">
                                    {entries
                                        .filter(
                                            (e) =>
                                                !e.streamTeamMember.isAcademy,
                                        )
                                        .map((entry) => (
                                            <button
                                                key={entry.streamTeamMember.id}
                                                onClick={() =>
                                                    handleClaimSelect(
                                                        entry.streamTeamMember
                                                            .id,
                                                    )
                                                }
                                                className="border-brand/50 hover:border-brand hover:bg-brand/20 w-full rounded border bg-black/30 px-4 py-3 text-left"
                                            >
                                                <p className="text-brand font-semibold">
                                                    {convertLetters(
                                                        entry.streamTeamMember
                                                            .player.name,
                                                    )}
                                                </p>
                                                <p className="text-xs text-slate-400">
                                                    @
                                                    {entry.streamTeamMember
                                                        .twitchUsername ||
                                                        "Unknown"}
                                                </p>
                                            </button>
                                        ))}
                                </div>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="mt-4 w-full rounded border border-slate-600 bg-slate-700/30 px-4 py-2 text-slate-300 hover:bg-slate-700/50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : null}

            {/* Runestone Cabinet Grid */}
            <div className="w-full">
                <div className="grid grid-cols-2 justify-center gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                    {/* Regular Runestone Cards */}
                    {entries
                        .filter((e) => !e.streamTeamMember.isAcademy)
                        .map((entry) => (
                            <RunestoneCard
                                key={entry.streamTeamMember.id}
                                entry={entry}
                            />
                        ))}

                    {/* Academy Runestone Card - Aggregated (Always Last) */}
                    {(() => {
                        const academyEntries = entries.filter(
                            (e) => e.streamTeamMember.isAcademy,
                        );
                        if (academyEntries.length === 0) return null;

                        const totalAcademyRedemptions = academyEntries.reduce(
                            (sum, e) => sum + e.redemptionCount,
                            0,
                        );

                        // Use the first academy runestone for the display (they all share the same academy runestone)
                        const firstAcademyEntry = academyEntries[0];

                        return (
                            <div
                                className={classNamesExtended(
                                    "border-brand relative aspect-square w-full max-w-32.5 overflow-hidden rounded border-4 sm:max-w-none",
                                    {
                                        "border-gray-300":
                                            totalAcademyRedemptions === 0,
                                    },
                                )}
                                title={`Academy Runestone - ${totalAcademyRedemptions} total redemptions`}
                            >
                                <div className="relative h-full w-full bg-black/20 p-2">
                                    <div className="flex h-full flex-col items-center justify-center gap-2">
                                        {firstAcademyEntry.runestone
                                            ?.imageUrl ? (
                                            <img
                                                src={
                                                    firstAcademyEntry.runestone
                                                        .imageUrl
                                                }
                                                alt="Academy Runestone"
                                                className="max-h-[50%] max-w-full object-contain"
                                            />
                                        ) : (
                                            <div className="flex h-[50%] w-full items-center justify-center rounded bg-slate-800/60 px-2 text-center text-xs text-slate-300">
                                                No Runestone
                                            </div>
                                        )}
                                        <div className="flex flex-col items-center gap-1">
                                            <p className="text-center text-xs font-semibold text-slate-100 sm:text-sm">
                                                Academy
                                            </p>
                                            <p className="text-stats text-center text-lg font-bold">
                                                {formatNumber(
                                                    totalAcademyRedemptions,
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
}
